import type { AnalyticsEvent } from "./events";
import { analyticsEnabled, loadProviders } from "./loader";
import { PROVIDERS, type Provider } from "./providers";
import type { PixelConfig } from "../api/content";

/** Потолок буфера: при заблокированных скриптах он иначе рос бы вечно. */
const MAX_BUFFERED = 50;

let started = false;
let ready = false;
const buffered: AnalyticsEvent[] = [];
/** Площадки, у которых в админке есть хотя бы один активный пиксель. */
let activeProviders: Provider[] = [];

/** Группирует пиксели из CMS по площадкам; неизвестные ключи и пустые ID
 *  отбрасываются — у старого фронта нет кода для новой площадки. */
function resolveActive(pixels: readonly PixelConfig[]) {
  return PROVIDERS.map((provider) => ({
    provider,
    ids: Array.from(
      new Set(
        pixels
          .filter((px) => px.provider === provider.key && px.id)
          .map((px) => px.id),
      ),
    ),
  })).filter((entry) => entry.ids.length > 0);
}

/** Ставит загрузку SDK в очередь на idle. Вызывается один раз за жизнь
 *  страницы из useRouteAnalytics, когда пришёл контент; повторные вызовы
 *  безвредны (смена пикселей в админке подхватится со следующей загрузки). */
export function startAnalytics(pixels: readonly PixelConfig[]): void {
  if (started || !analyticsEnabled()) return;
  started = true;
  const active = resolveActive(pixels);
  activeProviders = active.map((entry) => entry.provider);
  loadProviders(active, () => {
    ready = true;
    const pending = buffered.splice(0, buffered.length);
    pending.forEach(deliver);
  });
}

/** Единственный способ отправить событие. Пока SDK не загрузились, событие
 *  копится в буфере и уходит после — так первый page_view не теряется. */
export function track(event: AnalyticsEvent): void {
  if (!analyticsEnabled()) return;
  if (!ready) {
    if (buffered.length < MAX_BUFFERED) buffered.push(event);
    return;
  }
  deliver(event);
}

function deliver(event: AnalyticsEvent): void {
  activeProviders.forEach((p) => {
    try {
      p.send?.(event);
    } catch {
      // Отсутствующий SDK — нормальное состояние, а не ошибка: у части
      // посетителей его вырезает блокировщик рекламы.
    }
  });
}
